import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import ChartTooltip, { getTooltipHTML } from "../ChartTooltip";
import Loader from "../../Loader/Loader";
import GraphDownloadButton from "../../Button/GraphDownloadButton/GraphDownloadButton";
import { wrapText } from "../ChartUtil";
import AboutChartToggle from "../../AboutChartToggle/AboutChartToggle";
import "./HorizontalBarChart.scss";
import { Collapse } from "bootstrap/dist/js/bootstrap.bundle.min";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleMinus, faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import DataNotFound from "../../DataNotFound/DataNotFound";
import SomethingWentWrong from "../../SomethingWentWrong/SomethingWentWrong";
import ErrorBoundary from "../../ErrorBoundary/ErrorBoundary";

function rightRoundedPath(x, y, width, height, radius) {
  // const r = Math.min(radius, height / 2, width);
  const r = Math.min(radius, height / 2, width / 2);
  return `
    M${x},${y}
    H${x + width - r}
    A${r},${r} 0 0 1 ${x + width},${y + r}
    V${y + height - r}
    A${r},${r} 0 0 1 ${x + width - r},${y + height}
    H${x}
    Z
  `;
}

function getName(d) {
  return d.isChorusMember ? `${d.name}` : d.name;
}
function getId(d) {
  return d.id;
}

const nameLookup = {};

const HorizontalBarChartInner = ({
  chartData,
  chartConfig,
  title,
  notes,
  selectedNumberOfItem,
  colorArray = [],
  isHorizontalChartLoading,
  hasError = false,
}) => {
  const svgRef = useRef(null);
  const svgContainerRef = useRef(null);
  const tooltipRef = useRef(null);
  const wrapperRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 900, height: 600 });
  const [leftMargin, setLeftMargin] = useState(150);
  const [showFullName, setShowFullName] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);

  // Creates an object like: { "id123": "Apple", "id456": "Banana" }

  useEffect(() => {
    if (chartData && Array.isArray(chartData) && chartConfig) {
      setData(() => {
        return chartData.map((item, index) => {
          const id = item?.id || index;
          const rawName =
            item.isChorusMember && chartConfig?.y_field
              ? `*${item[chartConfig.y_field] ?? ""}`
              : chartConfig?.y_field
                ? (item[chartConfig.y_field] ?? "")
                : "";
          const name = typeof rawName === "string" ? rawName : String(rawName);
          nameLookup[id] = name;

          const rawWorkCount =
            chartConfig?.value_field ? item[chartConfig.value_field] : 0;
          const workCount =
            rawWorkCount != null && !isNaN(Number(rawWorkCount))
              ? Number(rawWorkCount)
              : 0;

          const base = {
            id: id,
            rorId: item?.rorId || null,
            name: name,
            workCount: workCount,
          };

          if ("isChorusMember" in item) {
            base.isChorusMember = item.isChorusMember;
          }

          return base;
        });
      });
    } else if (!chartData || !Array.isArray(chartData) || !chartConfig) {
      // API error, wrong response shape, or missing config — clear data so we show DataNotFound
      setData([]);
    }
  }, [chartData, chartConfig]);

  useEffect(() => {
    // When a caller drives loading externally (isHorizontalChartLoading is a
    // real boolean, not undefined), trust it — `data` briefly becomes `[]`
    // (truthy!) before the real chartData/chartConfig prop arrives, so a
    // data-truthiness check alone would flip isLoading false too early.
    if (isHorizontalChartLoading !== undefined) {
      setIsLoading(isHorizontalChartLoading);
    } else {
      setIsLoading(!data);
    }
  }, [data, isHorizontalChartLoading]);

  // -----------------------
  // Resize Observer
  // -----------------------
  useEffect(() => {
    if (data && data.length) {
      const maxLength = (data || []).reduce((max, item) => {
        return (item?.name?.length || 0) > max ? (item?.name?.length || 0) : max;
      }, 0);

      const calculatedNameWidth = maxLength * 7.2 + 20;

      if (showFullName) {
        setLeftMargin(calculatedNameWidth);
      } else {
        setLeftMargin(150);
      }

      const resizeObserver = new ResizeObserver((entries) => {
        if (!entries || !entries.length) return;
        const { width } = entries[0].contentRect;
        setDimensions({
          width:
            showFullName && calculatedNameWidth > (3 * width) / 4
              ? width + calculatedNameWidth / 2
              : width,
          height:
            data?.length <= 15
              ? data?.length < 5
                ? data?.length * 60 + 60
                : 600
              : 1000,
        }); //either 10 or 50
      });

      resizeObserver.observe(wrapperRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [selectedNumberOfItem, data, showFullName]);

  // useEffect(()=>{},[showFullName])

  // -----------------------
  // Chart
  // -----------------------

  useEffect(() => {
    if (!data || !data.length) return;
    const { width, height } = dimensions;
    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 30, right: 65, bottom: 50, left: leftMargin };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.workCount) || 0])
      .nice()
      .range([0, innerWidth]);

    const y = d3
      .scaleBand()
      .domain(data.map((d) => getId(d)))
      .range([0, innerHeight])
      .paddingInner(0.4)
      .paddingOuter(0.2);

    const color = d3
      .scaleOrdinal()
      .domain(data.map((d) => getId(d)))
      .range([
        ...colorArray,
        // "#067647",
        // "#0E6F90",
        // "#273F87",
        // "#7AE582",
        // "#DBB42C",
        // "#4F7A20",
        // "#6927DA",
        // "#9E165F",
        // "#97180C",
        // "#85490E",
        // ...d3.schemeTableau10,
      ]);

    const yGrid = d3.axisLeft(y).tickSize(-innerWidth).tickFormat("");

    svg
      .append("g")
      .attr("class", "y-grid")
      .attr(
        "transform",
        `translate(${margin.left},${margin.top - y.step() / 2})`,
      )
      .call(yGrid);

    svg.select(".y-grid").lower();

    svg.selectAll(".y-grid line").attr("stroke", "#DFE2E4");

    svg.selectAll(".y-grid path").remove();

    /* BARS */
    g.selectAll("path.bar")
      .data(data)
      .enter()
      .append("path")
      .attr("class", "bar")
      .attr("transform", (d) => `translate(0, ${y(getId(d))})`)
      .attr("fill", (d) => color(getId(d)))
      .attr("d", (d) =>
        rightRoundedPath(
          0,
          0,
          0, // start width (for animation)
          y.bandwidth(),
          y.bandwidth() / 2,
        ),
      )
      .on("mousemove", (event, d) => {
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const tooltip = d3.select(tooltipRef.current);

        // set content first, then measure — otherwise height/width are stale
        tooltip
          .style("opacity", 0)
          .html(
            getTooltipHTML({
              Category: getName(d),
              Value: d.workCount,
            }),
          );

        const { width, height } = tooltip.node().getBoundingClientRect();

        let x = event.clientX - wrapperRect.left + 12;
        let y = event.clientY - wrapperRect.top - height - 10;

        // keep tooltip inside the wrapper bounds
        if (x + width > wrapperRect.width) x -= width + 24;
        if (y < 0) y = event.clientY - wrapperRect.top + 10;

        tooltip
          .style("opacity", 1)
          .style("left", `${x}px`)
          .style("top", `${y}px`);
      })
      .on("mouseleave", () => {
        d3.select(tooltipRef.current).style("opacity", 0);
      })
      // .transition()
      // .duration(800)
      .attr("d", (d) =>
        rightRoundedPath(
          0,
          0,
          Math.max(x(d.workCount ?? 0), 6), // minimum width safety
          y.bandwidth(),
          y.bandwidth() / 2,
        ),
      );

    /* LABELS */
    g.selectAll(".label")
      .data(data)
      .enter()
      .append("text")
      .attr("x", (d) => x(d.workCount ?? 0) + 6)
      .attr("y", (d) => y(getId(d)) + y.bandwidth() / 2)
      .attr("dy", "0.35em")
      .text((d) => `${(d.workCount ?? 0).toLocaleString("en-US")}`)
      // .style("font-size", "15px")
      .style("font-size", "13px")
      .style("font-weight", 600)
      .style("fill", "#000");

    /* AXES */
    const yAxis = g.append("g").call(d3.axisLeft(y));
    yAxis
      .selectAll("text")
      .style("font-size", "14px")
      .attr("dx", "-0.5em")
      .call(wrapText, margin.left - 24, 1, false, 0, nameLookup)
      // .style("cursor", "pointer")
      .on("click", () => {
        // setShowFullName((prev) => !prev);
      });

    yAxis
      .selectAll("text")
      .style("font-size", "14px")
      .append("title")
      .text((d) => {
        // console.log("Left axis data =>", nameLookup[d], nameLookup);
        return showFullName ? "Collapse" : nameLookup[d];
      });

    yAxis.selectAll(".domain").remove(); // removes vertical axis line
    yAxis.selectAll(".tick line").remove(); // removes tick marks

    //For fixed ticks value especially at start and end
    // const tickCount = Math.max(2, Math.floor(innerWidth / 80));
    // const [min, max] = x.domain();
    // let customTicks = x.ticks(tickCount);
    // if (customTicks[0] !== min) {
    //   customTicks = [min, ...customTicks.filter((t) => t > min)];
    // }
    // if (customTicks[customTicks.length - 1] !== max) {
    //   customTicks = [...customTicks.filter((t) => t < max), max];
    // }
    // g.append("g")
    //   .attr("transform", `translate(0,${innerHeight})`)
    //   .call(d3.axisBottom(x).tickValues(customTicks))
    //   .style("font-size", "15px");

    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x).ticks(Math.max(2, Math.floor(innerWidth / 90))))
      .style("font-size", "15px");
  }, [data, dimensions, leftMargin]);

  return (
    <div
      ref={wrapperRef}
      style={{ width: "100%", minWidth: "768px", position: "relative" }}
    >
      <div className="position-relative">
        <GraphDownloadButton
          svg={svgRef.current}
          fileName={title}
          jsonData={data}
        />
        {notes && (
          <>
            <hr className="" />
            {/* <div className="text-start">{notes}</div> */}
            <AboutChartToggle notes={notes} />
          </>
        )}
        <button
          className={`btn btn-outline-theme ${notes ? "mt-3" : "position-absolute top-0 start-0"}`}
          onClick={() => setShowFullName((prev) => !prev)}
          title={showFullName ? "Collapse" : "Expand"}
        >
          {showFullName ? "Collapse" : "Expand"}
        </button>
      </div>
      <div className="horizontal_bar_chart_svg_container" ref={svgContainerRef}>
        {isLoading ? (
          <Loader />
        ) : hasError ? (
          <SomethingWentWrong />
        ) : data && data.length > 0 ? (
          <svg ref={svgRef} />
        ) : (
          <DataNotFound />
        )}
      </div>
      <ChartTooltip ref={tooltipRef} />
    </div>
  );
};

const HorizontalBarChart = (props) => (
  <ErrorBoundary>
    <HorizontalBarChartInner {...props} />
  </ErrorBoundary>
);

export default HorizontalBarChart;
